// Browser-side client + TanStack Query hooks for the admin endpoints (spec/04 Admin).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'

// ---------- response shapes
export interface AdminSummary {
  users: { total: number; new7d: number; new30d: number }
  plans: { total: number; new30d: number; prdsGenerated: number }
  activeSubscriptions: { slug: string; name: string; count: number }[]
  revenue30d: { currency: string; gross: number; discount: number; count: number }[]
  llm30d: { costUsd: number; jobs: number; jobInputTokens: number; jobOutputTokens: number; chatMessages: number; chatInputTokens: number; chatOutputTokens: number }
  funnel30d: {
    signups: number
    plansCreated: number
    prdCompleted: number
    tasksVerified: number
    tasksDone: number
    evidenceRatio: number
    activation: number
    verifiedPerWeek: number
    shareViews: number
    exports: number
  }
}
export interface AdminDaily {
  day: string
  signups: number
  plans: number
  revenueIdr: number
  revenueUsd: number
  costUsd: number
  inputTokens: number
  outputTokens: number
}
export interface Paged {
  total: number
  page: number
  pageSize: number
}
export interface AdminUser {
  id: string
  email: string
  name: string
  image: string | null
  role: string
  locale: string
  emailVerified: boolean
  onboardedAt: string | null
  referralSource: string | null
  createdAt: string
  providers: string[]
  lastLoginAt: string | null
  lastSeenAt: string | null
  activeSessions: number
  subscription: { slug: string; name: string; status: string; expiresAt: string } | null
  plansCount: number
  lastPlanAt: string | null
  lastPlanTitle: string | null
  workspacesCount: number
  prdUsed: number
  chatUsed: number
  tokensCount: number
  tokenLastUsedAt: string | null
  paidIdr: number
  paidUsd: number
  chatMessages: number
}
export interface AdminUserStats {
  total: number; new7d: number; new30d: number
  active1d: number; active7d: number; active30d: number
  paid: number; admins: number; google: number; credential: number
  onboarded: number; withPlans: number; withTokens: number
}
export interface AdminUserDetail {
  user: AdminUser
  accounts: { provider: string; createdAt: string }[]
  sessions: { id: string; ip: string | null; userAgent: string | null; createdAt: string; updatedAt: string; expiresAt: string }[]
  subscriptions: { id: string; name: string; slug: string; status: string; startsAt: string; expiresAt: string; createdAt: string }[]
  transactions: { orderId: string; provider: string; amount: number; status: string; paymentType: string | null; paidAt: string | null; createdAt: string; planName: string | null }[]
  plans: { id: string; title: string; status: string; step: number; workspaceName: string | null; createdAt: string }[]
  workspaces: { id: string; name: string; hasWiki: boolean; codebaseStatus: string; createdAt: string }[]
  tokens: { id: string; name: string; prefix: string; kind: string; lastUsedAt: string | null; revokedAt: string | null; createdAt: string }[]
  usage: { periodStart: string; periodEnd: string; prdUsed: number; chatUsed: number; coachingUsed: number }[]
  credits: { kind: string; amount: number; remaining: number; source: string; createdAt: string }[]
  attribution: { source: string | null; medium: string | null; campaign: string | null; referrer: string | null; landingPath: string | null; createdAt: string }[]
}
export interface AdminUserQuery { q: string; page: number; role?: string; provider?: string; status?: string; active?: string; sort?: string }
export interface AdminPlanRow {
  id: string
  title: string
  status: string
  step: number
  language: string
  userId: string
  userEmail: string
  workspaceId: string
  workspaceName: string
  createdAt: string
  updatedAt: string
  costUsd: number
}
export interface AdminJob {
  id: string
  kind: string
  phase: string
  status: string
  totalFeatures: number
  doneFeatures: number
  error: string | null
  inputTokens: number
  outputTokens: number
  costUsd: number
  startedAt: string
  finishedAt: string | null
}
export interface AdminPlanDetail {
  plan: AdminPlanRow & { idea: string; userName: string; hasWiki: boolean; codebaseStatus: string; techStack: Record<string, string>; cycle: number }
  counts: { features: number; tasks: number; documents: number; chatMessages: number; attachments: number }
  jobs: AdminJob[]
}
export interface AdminPlanCost {
  jobs: Pick<AdminJob, 'id' | 'kind' | 'phase' | 'status' | 'inputTokens' | 'outputTokens' | 'costUsd' | 'startedAt' | 'finishedAt'>[]
  chat: { messages: number; inputTokens: number; outputTokens: number }
  totals: { inputTokens: number; outputTokens: number; costUsd: number }
}
export interface AdminTransaction {
  id: string
  orderId: string
  userEmail: string
  planName: string | null
  planSlug: string | null
  currency: string | null
  grossAmount: number
  discountAmount: number
  status: string
  paymentType: string | null
  provider: string
  voucherCode: string | null
  paidAt: string | null
  createdAt: string
}
export interface AdminTxSummary {
  byStatus: { status: string; count: number; net: number }[]
  byCurrency: { currency: string; paidTotal: number; count: number; paid30d: number }[]
  byPlan: { slug: string; name: string; currency: string; count: number; paidTotal: number }[]
}
export interface AdminMarketing {
  signupsBySource: { source: string | null; count: number; new30d: number }[]
  attribution: { source: string | null; medium: string | null; campaign: string | null; count: number; users: number }[]
  landingPaths: { landingPath: string | null; count: number }[]
  totals: { events: number; users: number }
}
export interface AdminChatRow {
  planId: string
  title: string
  userEmail: string
  messages: number
  inputTokens: number
  outputTokens: number
  lastAt: string
}
export interface AdminChatDetail {
  plan: { id: string; title: string; userEmail: string }
  messages: { id: string; role: string; scope: string | null; position: number; text: string; inputTokens: number | null; outputTokens: number | null; createdAt: string }[]
}
export interface AdminAttributionEvent {
  id: string
  userEmail: string | null
  source: string | null
  medium: string | null
  campaign: string | null
  referrer: string | null
  landingPath: string | null
  createdAt: string
}
export interface AdminFeedback {
  id: string
  userEmail: string | null
  userName: string | null
  context: string | null
  rating: number | null
  message: string | null
  createdAt: string
}
export interface AdminCampusLead {
  id: string
  email: string
  name: string | null
  institution: string
  role: string | null
  partnership: string | null
  track: string | null
  teams: number | null
  startTerm: string | null
  phone: string | null
  note: string | null
  locale: string | null
  status: string
  createdAt: string
}
export interface AdminPrompt {
  id: string
  key: string
  label: string
  content: string
  defaultContent: string
  model: string | null
  temperature: number | null
  version: number
  updatedAt: string
  updatedByEmail: string | null
  tier: 'fast' | 'strong' | null
  isModified: boolean
}
export interface ToolRow {
  id: string
  slug: string
  name: string
  logoUrl: string | null
  description: string | null
  overallScore: number | null
  isPublished: boolean
  sortOrder: number
  createdAt: string
  scoresCount?: number
  videosCount?: number
}
export interface ToolScore {
  id?: string
  criteria: string
  score: number
  note: string | null
}
export interface ToolVideo {
  id?: string
  youtubeId: string
  title: string | null
  position?: number
}
export interface ToolFull {
  tool: ToolRow
  scores: ToolScore[]
  videos: ToolVideo[]
}
export interface PublicTool extends ToolRow {
  scores: ToolScore[]
  videos: ToolVideo[]
}
export interface Voucher {
  id: string
  code: string
  kind: 'percent' | 'fixed'
  value: number
  appliesTo: string[] | null
  isAuto: boolean
  maxRedemption: number | null
  redeemedCount: number
  startsAt: string | null
  endsAt: string | null
  isActive: boolean
  createdAt: string
}
export interface VoucherInput {
  code: string
  kind: 'percent' | 'fixed'
  value: number
  appliesTo: string[] | null
  isAuto: boolean
  maxRedemption: number | null
  startsAt: string | null
  endsAt: string | null
  isActive: boolean
}
export interface AdminHealth {
  db: { ok: boolean; error?: string; latencyMs?: number }
  gateway: { ok: boolean; error?: string; latencyMs?: number; models?: number; baseUrl: string }
  time: string
}

// ---------- query hooks
const qs = (o: Record<string, string | number | undefined>) => {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== '') p.set(k, String(v))
  const s = p.toString()
  return s ? `?${s}` : ''
}

export const useAdminSummary = () => useQuery({ queryKey: ['admin', 'summary'], queryFn: () => api<AdminSummary>('/api/admin/dashboard/summary') })
export const useAdminDaily = (days = 30) => useQuery({ queryKey: ['admin', 'daily', days], queryFn: () => api<{ days: AdminDaily[] }>(`/api/admin/dashboard/daily${qs({ days })}`) })
export const useAdminHealth = () => useQuery({ queryKey: ['admin', 'health'], queryFn: () => api<AdminHealth>('/api/admin/dashboard/health'), staleTime: 30_000 })
export const useAdminUsers = (p: AdminUserQuery) => useQuery({ queryKey: ['admin', 'users', p], queryFn: () => api<Paged & { users: AdminUser[] }>(`/api/admin/dashboard/users${qs({ ...p })}`), placeholderData: (prev) => prev })
export const useAdminUserStats = () => useQuery({ queryKey: ['admin', 'user-stats'], queryFn: () => api<AdminUserStats>('/api/admin/dashboard/users/stats') })
export const useAdminUser = (id: string | null) => useQuery({ queryKey: ['admin', 'user', id], queryFn: () => api<AdminUserDetail>(`/api/admin/dashboard/users/${id}`), enabled: Boolean(id) })
export const useAdminPlans = (q: string, page: number) => useQuery({ queryKey: ['admin', 'plans', q, page], queryFn: () => api<Paged & { plans: AdminPlanRow[] }>(`/api/admin/dashboard/plans${qs({ q, page })}`), placeholderData: (prev) => prev })
export const useAdminPlan = (id: string | null) => useQuery({ queryKey: ['admin', 'plan', id], queryFn: () => api<AdminPlanDetail>(`/api/admin/dashboard/plans/${id}`), enabled: Boolean(id) })
export const useAdminPlanCost = (id: string | null) => useQuery({ queryKey: ['admin', 'plan-cost', id], queryFn: () => api<AdminPlanCost>(`/api/admin/dashboard/plans/${id}/cost`), enabled: Boolean(id) })
export const useAdminTransactions = (page: number) => useQuery({ queryKey: ['admin', 'transactions', page], queryFn: () => api<Paged & { transactions: AdminTransaction[] }>(`/api/admin/dashboard/transactions${qs({ page })}`), placeholderData: (prev) => prev })
export const useAdminTxSummary = () => useQuery({ queryKey: ['admin', 'tx-summary'], queryFn: () => api<AdminTxSummary>('/api/admin/dashboard/transaction-summary') })
export const useAdminMarketing = () => useQuery({ queryKey: ['admin', 'marketing'], queryFn: () => api<AdminMarketing>('/api/admin/dashboard/marketing') })
export const useAdminChats = (q: string, page: number) => useQuery({ queryKey: ['admin', 'chats', q, page], queryFn: () => api<Paged & { chats: AdminChatRow[] }>(`/api/admin/dashboard/chat${qs({ q, page })}`), placeholderData: (prev) => prev })
export const useAdminChat = (planId: string | null) => useQuery({ queryKey: ['admin', 'chat', planId], queryFn: () => api<AdminChatDetail>(`/api/admin/dashboard/chat/${planId}`), enabled: Boolean(planId) })
export const useAdminAttribution = (page: number) => useQuery({ queryKey: ['admin', 'attribution', page], queryFn: () => api<Paged & { events: AdminAttributionEvent[] }>(`/api/admin/attribution${qs({ page })}`), placeholderData: (prev) => prev })
export const useAdminFeedback = (page: number) => useQuery({ queryKey: ['admin', 'feedback', page], queryFn: () => api<Paged & { feedback: AdminFeedback[]; avgRating: number | null }>(`/api/admin/feedback${qs({ page })}`), placeholderData: (prev) => prev })
export const useAdminCampusLeads = (page: number) => useQuery({ queryKey: ['admin', 'campus-leads', page], queryFn: () => api<Paged & { leads: AdminCampusLead[]; teams: number; byPartnership: { partnership: string | null; n: number }[] }>(`/api/admin/campus-leads${qs({ page })}`), placeholderData: (prev) => prev })
export const useAdminPrompts = () => useQuery({ queryKey: ['admin', 'prompts'], queryFn: () => api<{ prompts: AdminPrompt[] }>('/api/admin/prompts/list') })
export const useAdminTools = () => useQuery({ queryKey: ['admin', 'tools'], queryFn: () => api<{ tools: ToolRow[] }>('/api/admin/tools/list') })
export const useAdminTool = (id: string | null) => useQuery({ queryKey: ['admin', 'tool', id], queryFn: () => api<ToolFull>(`/api/admin/tools/${id}`), enabled: Boolean(id) })
export const useAdminToolPlans = (id: string | null) => useQuery({ queryKey: ['admin', 'tool-plans', id], queryFn: () => api<{ plans: { id: string; title: string; userEmail: string; createdAt: string }[] }>(`/api/admin/tools/${id}/plans`), enabled: Boolean(id) })
export const useAdminVouchers = () => useQuery({ queryKey: ['admin', 'vouchers'], queryFn: () => api<{ vouchers: Voucher[] }>('/api/admin/vouchers') })
export const usePublicTools = () => useQuery({ queryKey: ['tools', 'public'], queryFn: () => api<{ tools: PublicTool[] }>('/api/tools/list') })

// ---------- mutations
const useInvalidating = <TArgs, TRes>(fn: (args: TArgs) => Promise<TRes>, keys: string[][]) => {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => keys.forEach((k) => qc.invalidateQueries({ queryKey: k })) })
}

export const useAdminPlanGenerate = () => useInvalidating((id: string) => api<{ ok: true; job: unknown }>(`/api/admin/dashboard/plans/${id}/generate`, { method: 'POST' }), [['admin', 'plan'], ['admin', 'plan-cost'], ['admin', 'plans']])
export const useAdminPlanRegenerateWiki = () => useInvalidating((id: string) => api<{ ok: true; workspaceId: string; status: string }>(`/api/admin/dashboard/plans/${id}/regenerate-wiki`, { method: 'POST' }), [['admin', 'plan']])
export const useAdminPromptSave = () => useInvalidating((b: { key: string; content: string; model?: string | null; temperature?: number | null }) => api<{ prompt: AdminPrompt }>('/api/admin/prompts/save', { method: 'POST', json: b }), [['admin', 'prompts']])
export const useAdminPromptReset = () => useInvalidating((key: string) => api<{ prompt: AdminPrompt }>('/api/admin/prompts/reset', { method: 'POST', json: { key } }), [['admin', 'prompts']])
export type ToolInput = { name: string; slug?: string; description?: string | null; logoUrl?: string | null; isPublished?: boolean; sortOrder?: number }
export const useAdminToolCreate = () => useInvalidating((b: ToolInput) => api<{ tool: ToolRow }>('/api/admin/tools/create', { method: 'POST', json: b }), [['admin', 'tools'], ['tools']])
export const useAdminToolUpdate = () => useInvalidating(({ id, ...b }: Partial<ToolInput> & { id: string }) => api<{ tool: ToolRow }>(`/api/admin/tools/${id}`, { method: 'PATCH', json: b }), [['admin', 'tools'], ['admin', 'tool'], ['tools']])
export const useAdminToolDelete = () => useInvalidating((id: string) => api<{ ok: true }>(`/api/admin/tools/${id}`, { method: 'DELETE' }), [['admin', 'tools'], ['tools']])
export const useAdminToolScoresSave = () => useInvalidating(({ id, scores }: { id: string; scores: ToolScore[] }) => api<{ scores: ToolScore[] }>(`/api/admin/tools/${id}/scores`, { method: 'PUT', json: { scores } }), [['admin', 'tools'], ['admin', 'tool'], ['tools']])
export const useAdminToolVideosSave = () => useInvalidating(({ id, videos }: { id: string; videos: ToolVideo[] }) => api<{ videos: ToolVideo[] }>(`/api/admin/tools/${id}/videos`, { method: 'PUT', json: { videos } }), [['admin', 'tool'], ['admin', 'tools'], ['tools']])
export async function uploadToolLogo(file: File) {
  const fd = new FormData()
  fd.append('file', file)
  return api<{ url: string; file: string }>('/api/admin/tools/upload-url', { method: 'POST', body: fd })
}
export const useAdminVoucherCreate = () => useInvalidating((b: VoucherInput) => api<{ voucher: Voucher }>('/api/admin/vouchers', { method: 'POST', json: b }), [['admin', 'vouchers']])
export const useAdminVoucherUpdate = () => useInvalidating(({ id, ...b }: Partial<VoucherInput> & { id: string }) => api<{ voucher: Voucher }>(`/api/admin/vouchers/${id}`, { method: 'PATCH', json: b }), [['admin', 'vouchers']])
export const useAdminVoucherDelete = () => useInvalidating((id: string) => api<{ ok: true }>(`/api/admin/vouchers/${id}`, { method: 'DELETE' }), [['admin', 'vouchers']])
export const useAdminSetUserRole = () => useInvalidating((a: { id: string; role: 'admin' | 'user' }) => api<{ ok: true; user: { id: string; role: string } }>(`/api/admin/dashboard/users/${a.id}`, { method: 'PATCH', json: { role: a.role } }), [['admin', 'user'], ['admin', 'users'], ['admin', 'user-stats']])
export interface AdminMe { isOwner: boolean; planCatalog: { id: string; slug: string; name: string; currency: string; price: number; durationDays: number }[] }
export const useAdminMe = () => useQuery({ queryKey: ['admin', 'me'], queryFn: () => api<AdminMe>('/api/admin/dashboard/me'), staleTime: 5 * 60_000 })
export const useAdminSetUserPlan = () => useInvalidating((a: { id: string; slug: string; durationDays?: number; note?: string }) => api<{ ok: true; plan: { slug: string; expiresAt: string | null } }>(`/api/admin/dashboard/users/${a.id}/plan`, { method: 'PUT', json: { slug: a.slug, durationDays: a.durationDays, note: a.note } }), [['admin', 'user'], ['admin', 'users'], ['admin', 'user-stats'], ['admin', 'summary']])
export const useAdminCreateUser = () => useInvalidating((a: { email: string; name?: string; slug?: string; durationDays?: number; role?: 'admin' | 'user' }) => api<{ ok: true; user: { id: string; email: string; name: string; plan: { slug: string; expiresAt: string | null } } }>('/api/admin/dashboard/users', { method: 'POST', json: a }), [['admin', 'users'], ['admin', 'user-stats']])
export const useAdminRevokeUserSessions = () => useInvalidating((id: string) => api<{ ok: true; revoked: number }>(`/api/admin/dashboard/users/${id}/sessions`, { method: 'DELETE' }), [['admin', 'user'], ['admin', 'users'], ['admin', 'user-stats']])
